import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import useColleges from '../../../utils/useColleges';
import './DailyReport.css';

const DailyReport = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = isSuperAdmin || user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();

  const getTodayDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterFeeName, setFilterFeeName] = useState('');
  const [filterDate, setFilterDate] = useState(getTodayDate());

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCollege) params.college = filterCollege;
      const res = await api.get('/fees/daily-report', { params });
      setTransactions(res.data.data || []);
    } catch (err) {
      console.error(err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTransactions(); }, [filterCollege]); // eslint-disable-line

  useEffect(() => {
    if (!isAdmin && !isSuperAdmin && colleges.length > 0) setFilterCollege(String(colleges[0]._id));
    else if (isAdmin && !isSuperAdmin && colleges.length === 1) setFilterCollege(String(colleges[0]._id));
  }, [isAdmin, isSuperAdmin, colleges]); // eslint-disable-line

  const uniqueSessions = useMemo(() => {
    const map = new Map();
    transactions.forEach(t => {
      const s = t.session;
      if (s) map.set(s._id || s, s.name || s);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [transactions]);

  const uniqueClasses = useMemo(() => {
    const map = new Map();
    transactions.forEach(t => {
      const c = t.student?.class;
      if (c) {
        const id = c._id || c;
        const label = (c.name || c) + (c.medium ? ` (${c.medium})` : '');
        map.set(id, label);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [transactions]);

  const uniqueBatches = useMemo(() => {
    const set = new Set();
    transactions.forEach(t => { if (t.student?.admissionYear) set.add(t.student.admissionYear); });
    return Array.from(set).sort((a, b) => b - a);
  }, [transactions]);

  const uniqueFeeNames = useMemo(() => {
    const set = new Set();
    transactions.forEach(t => { if (t.feeName) set.add(t.feeName); });
    return Array.from(set).sort();
  }, [transactions]);

  const filtered = useMemo(() => {
    let list = transactions;

    if (filterCollege) list = list.filter(t => String(t.college?._id || t.college) === filterCollege);
    if (filterSession) list = list.filter(t => String(t.session?._id || t.session) === filterSession);
    if (filterClass)   list = list.filter(t => String(t.student?.class?._id || t.student?.class) === filterClass);
    if (filterBatch)   list = list.filter(t => String(t.student?.admissionYear) === filterBatch);
    if (filterFeeName) list = list.filter(t => t.feeName === filterFeeName);

    if (filterDate) {
      list = list.filter(t => {
        const d = t.paymentDate || t.createdAt;
        if (!d) return false;
        const dt = new Date(d);
        const dStr = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
        return dStr === filterDate;
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(t =>
        (t.student?.name || '').toLowerCase().includes(q) ||
        (t.student?.fatherName || '').toLowerCase().includes(q) ||
        (t.student?.admissionNo || '').toLowerCase().includes(q) ||
        (t.referenceNo || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, filterCollege, filterSession, filterClass, filterBatch, filterFeeName, filterDate, searchQuery]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(t => {
      const key = t.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(t);
    });
    return g;
  }, [filtered]);

  const grandCash   = filtered.reduce((s, t) => s + (t.cashAmount   || 0), 0);
  const grandOnline = filtered.reduce((s, t) => s + (t.onlineAmount || 0), 0);
  const grandTotal  = grandCash + grandOnline;

  const handleExportExcel = () => {
    const today = new Date().toISOString().split('T')[0];
    const headers = ['S.No', 'Paid On', 'Student Name', 'Father Name', 'Batch', 'Session', 'Year', 'Class', 'Fee Name', 'Cash (₹)', 'Online (₹)', 'Reference No', 'Total (₹)'];
    const colsCount = headers.length;

    const activeCollege = colleges.find(c => c._id === filterCollege)?.name || 'All Colleges';
    const activeSession = uniqueSessions.find(s => s.id === filterSession)?.name || 'All';
    const activeClass   = uniqueClasses.find(c => c.id === filterClass)?.name || 'All';
    const displayDate   = filterDate ? new Date(filterDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'All';
    const filtersText   = `Date: ${displayDate} | College: ${activeCollege} | Session: ${activeSession} | Class: ${activeClass} | Search: ${searchQuery || 'None'}`;

    const aoa = [
      ['DAILY FEE COLLECTION REPORT', ...Array(colsCount - 1).fill('')],
      [filtersText, ...Array(colsCount - 1).fill('')],
      Array(colsCount).fill(''),
    ];
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colsCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colsCount - 1 } },
    ];
    let currentRow = 3;

    Object.entries(grouped).forEach(([collegeName, list]) => {
      aoa.push([collegeName.toUpperCase(), ...Array(colsCount - 1).fill('')]);
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: colsCount - 1 } });
      currentRow++;
      aoa.push(headers);
      currentRow++;

      let colCash = 0, colOnline = 0, colTotal = 0;
      list.forEach((item, index) => {
        const student  = item.student || {};
        const cObj     = student.class || {};
        const className = (cObj.name || '-') + (cObj.medium ? ` (${cObj.medium})` : '');
        const cash     = item.cashAmount   || 0;
        const online   = item.onlineAmount || 0;
        const total    = cash + online;
        colCash += cash; colOnline += online; colTotal += total;
        const rowDate = item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
        aoa.push([
          index + 1, rowDate, student.name || '-', student.fatherName || '-',
          student.admissionYear || '-', item.session?.name || '-',
          student.currentYear || '-', className, item.feeName || '-',
          cash || '-', online || '-', item.referenceNo || '-', total,
        ]);
        currentRow++;
      });

      aoa.push(['', '', '', '', '', '', '', '', 'COLLEGE TOTAL:', colCash, colOnline, '', colTotal]);
      currentRow++;
      aoa.push(Array(colsCount).fill(''));
      currentRow++;
    });

    aoa.push(['', '', '', '', '', '', '', '', 'GRAND TOTAL:', grandCash, grandOnline, '', grandTotal]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;
    ws['!cols'] = [
      { wch: 6 }, { wch: 14 }, { wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 15 },
      { wch: 10 }, { wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 },
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } }, bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } }, right: { style: 'thin', color: { auto: 1 } },
    };
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };
        if (R === 0) {
          ws[ref].s = { font: { name: 'Arial', sz: 16, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E3A5F' } }, alignment: { vertical: 'center', horizontal: 'center' } };
        } else if (R === 1) {
          ws[ref].s = { font: { name: 'Arial', sz: 10, italic: true, color: { rgb: '2C3E50' } }, fill: { fgColor: { rgb: 'F1F5F9' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v.includes('TOTAL:')) {
          ws[ref].s = { font: { name: 'Arial', sz: 11, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'D97706' } }, alignment: { vertical: 'center', horizontal: C === 7 ? 'right' : 'center' }, border };
        } else if (ws[ref].v && typeof ws[ref].v === 'string' && ws[ref].v === ws[ref].v.toUpperCase() && C === 0 && !headers.includes(ws[ref].v) && R > 2) {
          ws[ref].s = { font: { name: 'Arial', sz: 13, bold: true, color: { rgb: '1E3A5F' } }, fill: { fgColor: { rgb: 'E2E8F0' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (headers.includes(ws[ref].v)) {
          ws[ref].s = { font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '34495E' } }, alignment: { vertical: 'center', horizontal: 'center' }, border };
        } else if (R > 2 && ws[ref].v !== '') {
          ws[ref].s = { font: { name: 'Arial', sz: 9 }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DailyReport');
    XLSX.writeFile(wb, `Daily_Fee_Report_${today}.xlsx`);
  };

  const handleClearFilters = () => {
    setFilterSession(''); setFilterClass(''); setFilterBatch('');
    setFilterFeeName(''); setSearchQuery('');
    setFilterDate(getTodayDate());
  };

  return (
    <div className="daily-paid-report-container">
      <div className="daily-paid-report-header-row">
        <h2 className="daily-paid-report-title">Daily Fee Report</h2>
        <div className="daily-paid-report-export-btns">
          <button className="daily-paid-report-btn-excel" onClick={handleExportExcel} disabled={filtered.length === 0}>
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {filtered.length > 0 && (
        <div className="daily-paid-report-summary-strip">
          <span>Showing <strong>{filtered.length}</strong> transactions</span>
          <div className="daily-paid-report-summary-totals">
            <span className="daily-paid-report-summary-cash">Cash: ₹ {grandCash.toLocaleString()}</span>
            <span className="daily-paid-report-summary-online">Online: ₹ {grandOnline.toLocaleString()}</span>
            <span className="daily-paid-report-summary-due">Grand Total: <strong>₹ {grandTotal.toLocaleString()}</strong></span>
          </div>
        </div>
      )}

      <div className="daily-paid-report-controls">
        <div className="daily-paid-report-search-wrapper">
          <FaSearch className="daily-paid-report-search-icon" />
          <input
            type="text"
            className="daily-paid-report-search-input"
            placeholder="Search name, adm. no, ref..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <input
          type="date"
          className="daily-paid-report-select"
          value={filterDate}
          onChange={e => setFilterDate(e.target.value)}
          title="Filter by Date"
        />

        {(isAdmin || isSuperAdmin) && (
          <select className="daily-paid-report-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="daily-paid-report-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {uniqueSessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="daily-paid-report-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {uniqueClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select className="daily-paid-report-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {uniqueBatches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select className="daily-paid-report-select" value={filterFeeName} onChange={e => setFilterFeeName(e.target.value)}>
          <option value="">All Fee Names</option>
          {uniqueFeeNames.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        {(filterSession || filterClass || filterBatch || filterFeeName || searchQuery || filterDate !== getTodayDate()) && (
          <button className="daily-paid-report-btn-clear" onClick={handleClearFilters}>✕ Clear</button>
        )}
      </div>

      {loading ? (
        <div className="daily-paid-report-state-msg">Loading daily reports...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="daily-paid-report-state-msg daily-paid-report-empty">No daily fee records found for the selected filters.</div>
      ) : (
        <div className="daily-paid-report-grouped-container">
          {Object.entries(grouped).map(([collegeName, list]) => {
            let colCash = 0, colOnline = 0, colTotal = 0;
            return (
              <div key={collegeName} className="daily-paid-report-table-group">
                <h3 className="daily-paid-report-group-header">
                  🏫 {collegeName}
                  <span className="daily-paid-report-group-count">{list.length} transactions</span>
                </h3>
                <div className="daily-paid-report-table-wrapper">
                  <table className="daily-paid-report-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th className="daily-paid-report-col-date">Paid On</th>
                        <th className="daily-paid-report-align-left">Student Name</th>
                        <th className="daily-paid-report-col-fname">Father's Name</th>
                        <th className="daily-paid-report-col-batch">Batch</th>
                        <th className="daily-paid-report-col-session">Session</th>
                        <th className="daily-paid-report-col-year">Year</th>
                        <th className="daily-paid-report-col-class">Class</th>
                        <th className="daily-paid-report-col-feename">Fee Name</th>
                        <th className="daily-paid-report-col-cash">Cash (₹)</th>
                        <th className="daily-paid-report-col-online">Online (₹)</th>
                        <th className="daily-paid-report-col-total">Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((item, i) => {
                        const rowId = String(item._id || i);
                        const student  = item.student || {};
                        const classObj = student.class || {};
                        const className = classObj.name || '—';
                        const medium    = classObj.medium ? ` (${classObj.medium})` : '';
                        const cash      = item.cashAmount   || 0;
                        const online    = item.onlineAmount || 0;
                        const total     = cash + online;
                        const refNos    = item.referenceNo || '';
                        const hasMultiRef = refNos.includes(',');

                        colCash += cash; colOnline += online; colTotal += total;

                        return (
                          <React.Fragment key={rowId}>
                            <tr className="daily-paid-report-main-row">
                              <td>{i + 1}</td>
                              <td className="daily-paid-report-col-date">
                                {item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                              </td>
                              <td className="daily-paid-report-align-left">
                                <div className="daily-paid-report-name-wrapper" onClick={() => setExpandedRow(expandedRow === rowId ? null : rowId)}>
                                  <div>
                                    <span style={{ fontWeight: 600 }}>{student.name || '—'}</span>
                                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{student.admissionNo || ''}</div>
                                  </div>
                                  <button className="daily-paid-report-expand-btn">
                                    {expandedRow === rowId ? <FaChevronUp /> : <FaChevronDown />}
                                  </button>
                                </div>
                              </td>
                              <td className="daily-paid-report-col-fname">{student.fatherName || '—'}</td>
                              <td className="daily-paid-report-col-batch">
                                <span className="daily-paid-report-badge">{student.admissionYear || '—'}</span>
                              </td>
                              <td className="daily-paid-report-col-session">{item.session?.name || '—'}</td>
                              <td className="daily-paid-report-col-year">{student.currentYear || '—'}</td>
                              <td className="daily-paid-report-col-class">
                                <span style={{ fontWeight: 600 }}>{className}</span>
                                {medium && <div style={{ fontSize: '0.72rem', color: '#7c3aed' }}>{medium}</div>}
                              </td>
                              <td className="daily-paid-report-col-feename">
                                <span className="daily-paid-report-feename-badge">{item.feeName || '—'}</span>
                              </td>
                              <td className="daily-paid-report-col-cash daily-paid-report-amt-cash">
                                {cash ? cash.toLocaleString() : '—'}
                              </td>
                              <td className="daily-paid-report-col-online daily-paid-report-amt-online">
                                {online ? online.toLocaleString() : '—'}
                              </td>
                              <td className="daily-paid-report-col-total daily-paid-report-amt-totalfee">
                                {total.toLocaleString()}
                              </td>
                            </tr>

                            {expandedRow === rowId && (
                              <tr className="daily-paid-report-expanded-row">
                                <td colSpan="12" style={{ padding: 0 }}>
                                  <div className="daily-paid-report-expanded-content">
                                    <div className="daily-paid-report-expanded-grid">
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-fname">
                                        <span className="daily-paid-report-exp-label">Father's Name</span>
                                        <span className="daily-paid-report-exp-value">{student.fatherName || '—'}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-batch">
                                        <span className="daily-paid-report-exp-label">Batch</span>
                                        <span className="daily-paid-report-exp-value">{student.admissionYear || '—'}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-session">
                                        <span className="daily-paid-report-exp-label">Session</span>
                                        <span className="daily-paid-report-exp-value">{item.session?.name || '—'}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-year">
                                        <span className="daily-paid-report-exp-label">Year</span>
                                        <span className="daily-paid-report-exp-value">{student.currentYear || '—'}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-class">
                                        <span className="daily-paid-report-exp-label">Class</span>
                                        <span className="daily-paid-report-exp-value">{className}{medium}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-feename">
                                        <span className="daily-paid-report-exp-label">Fee Name</span>
                                        <span className="daily-paid-report-exp-value">{item.feeName || '—'}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-cash">
                                        <span className="daily-paid-report-exp-label">Cash</span>
                                        <span className="daily-paid-report-exp-value daily-paid-report-amt-cash">₹ {cash.toLocaleString()}</span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-online">
                                        <span className="daily-paid-report-exp-label">Online</span>
                                        <span className="daily-paid-report-exp-value daily-paid-report-amt-online">₹ {online.toLocaleString()}</span>
                                      </div>
                                      {/* Ref No — always visible in expand, full width, chips if multiple */}
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-ref">
                                        <span className="daily-paid-report-exp-label">Reference No</span>
                                        <span className="daily-paid-report-exp-value">
                                          {refNos ? (
                                            hasMultiRef ? (
                                              <div className="daily-paid-report-multi-ref">
                                                {refNos.split(',').map((r, idx) => (
                                                  <span key={idx} className="daily-paid-report-ref-chip">{r.trim()}</span>
                                                ))}
                                              </div>
                                            ) : <span className="daily-paid-report-ref-chip">{refNos}</span>
                                          ) : <span style={{color:'#94a3b8', fontWeight:400}}>No reference</span>}
                                        </span>
                                      </div>
                                      <div className="daily-paid-report-exp-item daily-paid-report-exp-total">
                                        <span className="daily-paid-report-exp-label">Total</span>
                                        <span className="daily-paid-report-exp-value daily-paid-report-amt-totalfee">₹ {total.toLocaleString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <tr className="daily-paid-report-footer-row">
                        <td colSpan="9" style={{ textAlign: 'right', fontWeight: 700, color: '#334155' }}>COLLEGE TOTAL:</td>
                        <td className="daily-paid-report-col-cash" style={{ fontWeight: 700, color: '#16a34a' }}>₹ {colCash.toLocaleString()}</td>
                        <td className="daily-paid-report-col-online" style={{ fontWeight: 700, color: '#3b82f6' }}>₹ {colOnline.toLocaleString()}</td>
                        <td className="daily-paid-report-col-total" style={{ fontWeight: 800, color: '#1e40af' }}>₹ {colTotal.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DailyReport;
